import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Workspace, moveWorkspaceToTrash as trashWorkspace, loadDb } from "../data/schema";
import { generateOrganicPattern } from "../../../utils/patternGenerator";
import { DeviceSize } from "constants/deviceSize";
import { Button } from "ui-kit/Button";
import { FormattedMessage } from "react-intl";

type Props = {
  workspaces: Workspace[];
  workspaceSearch: string;
  onChangeSearch: (value: string) => void;
  onOpenCreate: () => void;
  onSelect: (workspaceId: string) => void;
  onMoveToTrash: (workspaceId: string) => void | Promise<void>;
  isLoading?: boolean;
   onOpenComposeModal?: () => void;
  hasPlayer?: boolean;
};

export const WorkspacesList: React.FC<Props> = ({
  workspaces,
  workspaceSearch,
  onChangeSearch,
  onOpenCreate,
  onSelect,
  onMoveToTrash,
  isLoading = false,
  onOpenComposeModal,
  hasPlayer,
}) => {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const selectedId = (() => { try { return localStorage.getItem('aiCompose.saveWorkspaceId'); } catch { return null; } })();

  const filtered = useMemo(() => {
    const query = workspaceSearch.toLowerCase();
    const db = loadDb();

    return workspaces.filter((workspace) => {
      const nameMatches = workspace.name.toLowerCase().includes(query);
      if (nameMatches || !query) {
        return true;
      }

      const tracks = workspace.trackIds
        .map(id => db.tracks[id])
        .filter((track): track is NonNullable<typeof track> => Boolean(track));

      return tracks.some(track => {
        const titleMatch = track.title?.toLowerCase().includes(query);
        const descriptionMatch = track.description?.toLowerCase().includes(query);
        const songDescriptionMatch = track.params?.songDescription?.toLowerCase().includes(query);
        const stylesDescriptionMatch = track.params?.stylesDescription?.toLowerCase().includes(query);
        return Boolean(titleMatch || descriptionMatch || songDescriptionMatch || stylesDescriptionMatch);
      });
    });
  }, [workspaces, workspaceSearch]);

  const handleSelectWorkspace = (workspaceId: string) => {
    onSelect(workspaceId);
    try {
      localStorage.setItem('aiCompose.saveWorkspaceId', workspaceId);
      localStorage.setItem('aiCompose.lastWorkspaceId', workspaceId);
    } catch {}
    window.dispatchEvent(new CustomEvent('workspaceSelected', { detail: { workspaceId: workspaceId } }));
  };

  return (
    <ListContainer $hasPlayer={hasPlayer}>
      <FixedHeader>
        <HeaderRow>
          <WorkspacesTitle>Workspaces{filtered.length ? ` (${filtered.length})` : ''}</WorkspacesTitle>
          <NewTopButton onClick={onOpenCreate}>+ New Workspace</NewTopButton>
        </HeaderRow>
        <SearchInput
          name="workspaceSearch"
          placeholder="Search"
          value={workspaceSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeSearch(e.target.value)}
        />
      </FixedHeader>
      <ScrollableContent>
        {isLoading && workspaces.length === 0 ? (
          <EmptyState>
            <EmptyText>Loading workspaces...</EmptyText>
          </EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState>
            {workspaceSearch.trim() ? (
              <EmptyText>No workspaces match “{workspaceSearch}”.</EmptyText>
            ) : (
              <>
            <EmptyText>You don't have a workspace yet. Create your first one!</EmptyText>
              </>
            )}
          </EmptyState>
        ) : (
          <List>
            {filtered.map(w => (
              <Item key={w.id} onClick={() => handleSelectWorkspace(w.id)} $selected={selectedId === w.id}>
                <Cover>
                  <CoverPattern $patternUrl={generateOrganicPattern(w.id)} />
                </Cover>
                <ItemContent>
                  <ItemTitle>{w.name}</ItemTitle>
                  <ItemMeta>
                  {w.trackIds.length} {w.trackIds.length === 1 ? 'Song' : 'Songs'}, {w.updatedAt ? new Date(w.updatedAt).toLocaleString() : w.createdAt ? new Date(w.createdAt).toLocaleString() : 'Recently'}
                  </ItemMeta>
                </ItemContent>
                <MoreWrapper>
                  <MoreButton onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === w.id ? null : w.id); }}>⋯</MoreButton>
                  {menuOpenId === w.id && (
                    <Menu onClick={(e) => e.stopPropagation()}>
                      <MenuItem onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); onMoveToTrash(w.id); }}>Move to trash</MenuItem>
                    </Menu>
                  )}
                </MoreWrapper>
              </Item>
            ))}
          </List>
        )}
      </ScrollableContent>
    </ListContainer>
  );
};

const ListContainer = styled.div<{ $hasPlayer?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0px 20px 0px 0px;
  flex: 1;
  box-sizing: border-box;
  height: 100%;
  @media (max-width: ${DeviceSize.TabletMedium}) {
  max-height: 80vh;
    padding: 0px;
  }
    max-height: 80vh;
`;

const FixedHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WorkspacesTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: #333;
`;

const SearchInput = styled.input`
  border-radius: 8px;
  font-size: 14px;
  background-color: #fff;
  height: 4.2vh;
  padding: 1vh 1vw;
  border: 1px solid #e9ecef;
  outline: none;
  box-sizing: border-box;
`;

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f4f4f4;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
    &:hover {
      background: #999;
    }
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    padding: 0px 0px 5vh 0px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 2.4vh;
  height: 100%;
  min-height: 50vh;
`;

const EmptyText = styled.div`
  color: #6c757d;
  font-size: 16px;
  text-align: center;
`;

const NewWorkspaceButton = styled.button`
  padding: 1.4vh 2vw;
  border-radius: 8px;
  border: none;
  background-color: #7987FF;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #6b7cff;
  }
`;

const NewTopButton = styled(NewWorkspaceButton)`
  padding: 1vh 1.2vw;
  border-radius: 18px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4vh;
  padding-bottom:10px;
`;

const Item = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.2vh;
  padding: 1.4vh;
  margin: 3px 10px;
  border-radius: 12px;
  background: ${p => p.$selected ? '#fff' : '#fff'};
  box-shadow: ${p => p.$selected ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'};
  transition: background 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #fff;
    box-shadow: ${p => p.$selected ? '0 4px 12px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.15)'};
  }
`;

const Cover = styled.div`
  position: relative;
  width: 12vh;
  height: 9vh;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
`;

const CoverPattern = styled.div<{ $patternUrl: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("${p => p.$patternUrl}");
  background-size: cover;
  background-position: center;
`;


const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const ItemTitle = styled.div`
  font-weight: 600;
  color: #333;
`;

const ItemMeta = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

const MoreWrapper = styled.div`
  position: relative;
`;

const MoreButton = styled.button`
  width: 4vh;
  height: 4vh;
  border-radius: 4vh;
  border: 1px solid #e9ecef;
  background: #fff;
  cursor: pointer;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 4vh;
    height: 4vh;
    border-radius: 50%;
  }
`;

const Menu = styled.div`
  position: absolute;
  right: 0;
  top: 36px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
  min-width: 150px;
  z-index: 10;
`;

const MenuItem = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  color: #333;
  &:hover { background: #f8f9ff; }
`;

export default WorkspacesList;


